const ATTRIBUTE = 'x-apple-data-detectors';

/** How long to keep watching a mount. Hydration is long over by then. */
const WATCH_MS = 10_000;

export type AppleDataDetectorEvidence = {
  /** Links iOS had already put inside the mount when hydration started. */
  beforeHydration: number;
  /** Links it added while hydration was running. */
  addedDuringHydration: number;
  /** What iOS took the text for — `telephone`, `calendar-event`, `address`, … */
  types: string[];
};

/**
 * Watches a mount for the links iOS data detectors insert, so a hydration mismatch report
 * can say whether iOS rewrote the server's markup before React got to it.
 *
 * iOS wraps what it recognises — a phone number, a date, a time range, an address — in an
 * `<a x-apple-data-detectors>`, which splits the text node it found. That only breaks
 * hydration when the text is one of several children: React then hydrates the text node on
 * its own, finds it cut short, and fails with "Text content does not match". An element whose
 * only child is the string is compared by its whole textContent, which the link leaves
 * unchanged, so it hydrates cleanly with the link still in it. Safari honours the
 * `format-detection` meta in layouts/v2/base.html, but an app's in-app browser runs whichever
 * detectors the app turned on, and whether it reads that tag is not documented.
 *
 * Both moments are counted because either can be the one: the links may be in place before
 * hydration starts, or arrive while it runs. Counting at report time instead would see
 * nothing — React re-renders the subtree after a mismatch, which throws the links away.
 *
 * Call the returned function for the evidence. It also stops the watch, which otherwise
 * ends on its own after WATCH_MS so a page that hydrates cleanly is not observed forever.
 */
export function watchAppleDataDetectors(root: HTMLElement): () => AppleDataDetectorEvidence {
  const types = new Set<string>();

  const collect = (el: Element) => {
    const type = el.getAttribute(`${ATTRIBUTE}-type`);

    if (type) {
      types.add(type);
    }
  };

  const existing = root.querySelectorAll(`[${ATTRIBUTE}]`);
  existing.forEach(collect);

  let added = 0;

  const record = (records: MutationRecord[]) => {
    records.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) {
          return;
        }

        const links = node.matches(`[${ATTRIBUTE}]`)
          ? [node]
          : Array.from(node.querySelectorAll(`[${ATTRIBUTE}]`));

        added += links.length;
        links.forEach(collect);
      });
    });
  };

  const observer = typeof MutationObserver === 'undefined' ? null : new MutationObserver(record);
  observer?.observe(root, { childList: true, subtree: true });

  const stop = () => {
    if (observer) {
      record(observer.takeRecords());
      observer.disconnect();
    }
  };

  const timer = setTimeout(stop, WATCH_MS);

  return () => {
    clearTimeout(timer);
    stop();

    return { beforeHydration: existing.length, addedDuringHydration: added, types: Array.from(types) };
  };
}
